<style scoped lang="sass">
.order_items_content,
.no_combo_detail
  flex: 1
  display: flex
  flex-direction: column
  margin-right: 20px

  &.combo_order_items_content
    margin-top: 6px

  .title
    font-size: 14px
    color: var(--menu-primary-text-colour)

  .detail,
  .no_combo_detail,
  .combo_detail
    line-height: 20px
    font-size: 14px
    color: var(--menu-primary-text-colour)

.normal_font
  display: flex
  flex-wrap: wrap

.expire
  .order_items_content .title,
  .order_items_content .detail
    text-decoration: line-through

  .error_text
    margin-top: 2px
    color: #B00620
    font-size: 12px

::v-deep

  .van-stepper__minus::before,
  .van-stepper__plus::before
    height: 2px
    border-radius: 2px

  .van-stepper__minus::after,
  .van-stepper__plus::after
    width: 2px
    border-radius: 2px
</style>

<template lang="pug">
.order_items_content(:class="[{ 'combo_order_items_content': isCombo }, { 'expire': isExpire }]")
  .title.b(v-if="data.upgradeProductName || data.name") {{ localizedName }} {{ localizedSizeName ? `(${localizedSizeName})` : '' }}
  .no_combo_detail(v-if="!isCombo")
    span.detail(
      v-for="item in data.modifications"
      :key="item.id"
      v-if="item.type === 'required' && !item.removeType"
    )
      span.each(
        v-for="(each, index) in computedOptions(item.options)"
        :key="each.id"
        v-if="each.checked"
      )
        span.mark.o(v-if="!index") {{ getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}
        span.name.o(v-if="index") {{ ', ' + getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}
    .error_text(v-if="isExpire") {{ expireErrorText }}
    span.detail(
      v-for="item in data.modifications"
    )
      span.each(
        v-for="(each, index) in computedOptions(item.options)"
        :key="each.id"
      )
        span(v-if="item.type !== 'required' && !item.removeType")
          span.mark.o(v-if="!index && item.id !== -1") {{ t('add_label', 'Add :') }} {{ getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}
          span.name.o(v-if="index && item.id !== -1") {{ ', ' + getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}
        span(v-if="item.removeType")
          span.mark.o(v-if="!index") {{ t('remove_label', 'Remove :') }} {{ getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}
          span.name.o(v-if="index") {{ ', ' + getLocalizedOptName(each) }}{{ each.number > 1 ? ' x' + each.number : '' }}

  .combo_detail(v-else)
    .required(v-if="data.addList")
      .normal_font(v-for="(items, i) in data.addList" :key="items.id")
        span(v-if="items.type !== 'optional'" v-for="(each, eachIndex) in items.items" :key="each.id")
          span.o5(v-if="!eachIndex") {{ getLocalizedOptName(each) }}{{ each.no > 1 ? ' x' + each.no : '' }}
          span.o5(v-if="eachIndex") {{ ', ' + getLocalizedOptName(each) }}{{ each.no > 1 ? ' x' + each.no : '' }}

    .normal_font(v-if="item.type === 'optional'" v-for="(item, i) in data.addList" :key="data.id + i" )
      span(v-for="(each, index) in item.items" :key="each.id")
        span.o5(v-if="!index") {{ t('add_label', 'Add:') + ' '}}
        span.o5(v-if="index") {{ ", " }}
        span.o5 {{ getLocalizedOptName(each) }}{{ each.no > 1 ? ' x' + each.no : '' }}

    .normal_font(v-if="item.type === 'optional' || 'required'" v-for="(item, i) in data.removeList" :key="data.id")
      span(v-for="(each, index) in item.items" :key="each.id")
        span.o5(v-if="!index") {{ t('remove_label', 'Remove:') + ' '}}
        span.o5(v-if="index") {{ ", " }}
        span.o5 {{ getLocalizedOptName(each) }}{{ each.no > 1 ? ' x' + each.no : '' }}
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapState } from 'vuex'
import { getLocalizedField, t } from '@/utils/language'
import { isMenuChangeMark } from '@/utils/menu/cartMarks'

@Component({
  props: {
    data: Object,
    isExpire: Object,
    isCombo: Boolean,
  },

  computed: {
    ...mapState({
      isOnline: (state) => state.app.isOnline,
      currentLanguage: state => state.language?.currentLanguage,
    }),

    localizedName() {
      return this.data.upgradeProductName || getLocalizedField(this.data, 'name') || this.data.name
    },

    // Get localized size name / 获取本地化的尺寸名称
    localizedSizeName() {
      if (!this.data.selectSize || !this.data.selectSize.name) return ''
      const _lang = this.currentLanguage // eslint-disable-line no-unused-vars
      return getLocalizedField(this.data.selectSize, 'name') || this.data.selectSize.name
    },

    /**
     * @returns {String} Invalid product prompt copy
     */
    /**
     * @returns {String} 失效商品的提示文案
     */
    expireErrorText() {
      // type = 1. It indicates that there is a shortage of stock
      // type = 1 时, 说明是缺库存
      if (this.isExpire.type === 1) return t('item_not_available', 'This item is currently not available')
      // 多版本菜单 B6（U-B6-2 review 第 1 轮 F2）：菜单变了 / 下单被拒按 id 标出来的行（标记带 source）不是「选的时间不行」，
      // 外卖系统也说「这件现在没有了」；老标记照旧
      else if (isMenuChangeMark(this.isExpire)) return t('item_no_longer_available', 'This item is no longer available')
      else if (this.isOnline) return t('unavailable_scheduled_time', 'Unavailable for your scheduled time')
      return t('item_no_longer_available', 'This item is no longer available')
    }
  }
})

class OrderItemsContent extends Vue {
  // Translation helper / 翻译辅助函数
  t(key, fallback) {
    const _lang = this.$lang // eslint-disable-line no-unused-vars
    return t(key, fallback)
  }

  // Get localized option name / 获取本地化的选项名称
  getLocalizedOptName(option) {
    const _lang = this.currentLanguage // eslint-disable-line no-unused-vars
    return getLocalizedField(option, 'name') || option.name
  }

  computedOptions(options) {
    const res = options.filter(item => item.checked)
    return res
  }
}

export default OrderItemsContent
</script>
